import { useCallback, useEffect, useRef, useState } from "react"

import { evaluateCaptureFreshness } from "../lib/capture-health"
import type { ISessionHealth } from "./useSessionKeepalive"

type THealthStatus = "nominal" | "warning" | "error"

interface IHealthCheck {
  status: THealthStatus
  label: string
  lastChecked: Date | null
  issues: string[]
}

interface IHealthCheckInputs {
  isRecording: boolean
  isPaused: boolean
  wsReadyState: number | null
  lastSegmentAtRef: React.MutableRefObject<number>
  lastAudioAtRef: React.MutableRefObject<number>
  sessionHealth: ISessionHealth
  isBatchMode: boolean
}

const CHECK_INTERVAL_MS = 5_000
const BACKEND_CHECK_INTERVAL_MS = 5 * 60 * 1000
const STALE_SEGMENT_THRESHOLD_MS = 60 * 1000
const BACKEND_TIMEOUT_MS = 3000

const INITIAL_STATE: IHealthCheck = {
  status: "nominal",
  label: "All systems nominal",
  lastChecked: null,
  issues: [],
}

async function checkBackend(): Promise<boolean> {
  try {
    const res = await fetch("/health", {
      signal: AbortSignal.timeout(BACKEND_TIMEOUT_MS),
      cache: "no-store",
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Runs a health check every 5 seconds during recording.
 * Capture freshness is the never-lose signal (MediaRecorder timeslices every ~2s).
 * Session / WebSocket / transcript / backend are still checked; backend reachability
 * is throttled to every 5 minutes so we don't hammer /health.
 */
export function useRecordingHealthCheck(inputs: IHealthCheckInputs): IHealthCheck {
  const { isRecording, isPaused, wsReadyState, lastSegmentAtRef, lastAudioAtRef, sessionHealth, isBatchMode } = inputs
  const [health, setHealth] = useState<IHealthCheck>(INITIAL_STATE)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastBackendCheckAtRef = useRef(0)
  const lastBackendOkRef = useRef(true)

  const runCheck = useCallback(async (): Promise<void> => {
    const issues: string[] = []

    // 1. Session check
    if (sessionHealth.status === "expired") {
      issues.push("Session expired — uploads will fail")
    } else if (sessionHealth.status === "error") {
      issues.push("Could not verify session")
    } else if (sessionHealth.hoursRemaining !== null && sessionHealth.hoursRemaining < 1) {
      issues.push(`Session expires in ${Math.round(sessionHealth.hoursRemaining * 60)} min`)
    }

    // 2. Capture freshness — the never-lose invariant. A quiet room still
    // emits 2s MediaRecorder timeslices; a gap means the mic/tab froze.
    const capture = evaluateCaptureFreshness({
      isRecording,
      isPaused,
      lastAudioAt: lastAudioAtRef.current,
      now: Date.now(),
    })
    if (capture.stalled) {
      issues.push(`No audio captured in ${capture.silentSec}s — keep this screen on`)
    }

    // 3. WebSocket check (streaming mode only)
    if (!isBatchMode) {
      if (wsReadyState !== WebSocket.OPEN) {
        issues.push("WebSocket disconnected — batch mode active")
      }

      // 4. Transcript freshness (streaming mode only)
      const msSinceLastSegment = Date.now() - lastSegmentAtRef.current
      if (lastSegmentAtRef.current > 0 && msSinceLastSegment > STALE_SEGMENT_THRESHOLD_MS) {
        issues.push(`No transcript received in ${Math.round(msSinceLastSegment / 1000)}s`)
      }
    }

    // 5. Backend reachability (throttled)
    const now = Date.now()
    if (now - lastBackendCheckAtRef.current >= BACKEND_CHECK_INTERVAL_MS) {
      lastBackendCheckAtRef.current = now
      lastBackendOkRef.current = await checkBackend()
    }
    if (!lastBackendOkRef.current) {
      issues.push("Backend unreachable")
    }

    const status: THealthStatus =
      capture.stalled ||
      issues.some((i) => i.includes("expired") || i.includes("unreachable") || i.includes("disconnected"))
        ? "error"
        : issues.length > 0
          ? "warning"
          : "nominal"

    const label = issues.length === 0 ? "All systems nominal" : issues[0]

    setHealth({ status, label, lastChecked: new Date(), issues })
  }, [isRecording, isPaused, wsReadyState, lastSegmentAtRef, lastAudioAtRef, sessionHealth, isBatchMode])

  useEffect(() => {
    if (!isRecording) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // Run immediately when recording starts
    void runCheck()

    intervalRef.current = setInterval(() => void runCheck(), CHECK_INTERVAL_MS)

    return (): void => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRecording, runCheck])

  return health
}
